import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { communicationUtilities } from '@incentivegames/ig-frontend-common/lib/utilities/communicationUtilities';

import EntryClosedOverlay from 'components/EntryClosedOverlay';
import PlayerCardWithFade, { PlayerCardWithFadeWidths } from 'components/PlayerCardWithFade';
import { allPlayersRevealed, revealTeamPlayer, getOppositionTeam, getReactAppNameEnv } from './WidgetCardContainerFunctions';
import { dreamteamUtilities } from 'utilities/dreamteamUtilities';
import { ReactGACategories } from 'constants/appEnums';
import { TFunction } from 'i18next';
import WidgetTeamFooter from 'components/WidgetTeamFooter';

import './WidgetCardContainer.scss';

interface IWidgetPlayer {
  teamMemberId: number;
  selnNumber: number;
  displayName: string;
  teamCode: string;
  revealed: boolean;
  eventParticipants: { participantCode: string }[];
}

interface IWidgetCardContainer {
  initTeam: IWidgetPlayer[];
  closed: boolean;
}

const WidgetCardContainer: React.FC<IWidgetCardContainer> = ({ initTeam, closed }) => {
  const { t } = useTranslation();
  const [team, setTeam] = useState(() => (closed ? initTeam.map((player) => ({ ...player, revealed: true })) : initTeam));
  const allRevealed = allPlayersRevealed(team);
  const roundUnavailable = team.length === 0;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const buttonType: string = closed ? 'learn-more' : 'join';

  useEffect(() => {
    if (allRevealed) buttonRef.current?.classList.add('widget-card-container__button--revealed');
  }, [allRevealed]);

  const onCardReveal = (selnNumber: number) => {
    setTeam(revealTeamPlayer(selnNumber, team));
    dreamteamUtilities.recordEventGA(ReactGACategories.widget, `User Revealed card ${selnNumber}`);

    try {
      communicationUtilities.sendPostMessage.tracking(`player reveals card ${selnNumber}`);
    } catch {
      dreamteamUtilities.recordEventGA(ReactGACategories.widget, `Error sending card reveal post message`);
    }
  };

  const onButtonClick = () => {
    if (!closed) {
      dreamteamUtilities.recordEventGA(ReactGACategories.widget, 'User Clicked Join');
      communicationUtilities.sendPostMessage.join();
    } else {
      dreamteamUtilities.recordEventGA(ReactGACategories.widget, 'User Clicked Learn More');
      communicationUtilities.sendPostMessage.howToPlay();
    }
  };

  return (
    <div className="widget-card-container">
      <div className="widget-card-container__inner">
        <EntryClosedOverlay show={closed} />
        <div className="widget-card-container__card-wrapper">{roundUnavailable ? roundUnavailableCards() : roundAvailableCards(team, onCardReveal)}</div>
        <div className="widget-card-container__footer">
          <div className="widget-card-container__primary-text">{roundUnavailable ? t('round-unavailable') : selectPromoText(t)}</div>
          {allRevealed && (
            <button ref={buttonRef} className={`widget-card-container__button widget-card-container__button--${buttonType}`} onClick={onButtonClick}>
              {t(buttonType)}
            </button>
          )}
        </div>
        <div className="widget-card-container__secondary-text">{t('tcs-apply-bet-responsibly-begameaware-18')}</div>
      </div>
    </div>
  );
};

const selectPromoText = (t: TFunction) => {
  const appName = getReactAppNameEnv();
  return appName?.match(/ng/i) ? t('widget-promo-text-nigeria') : t('widget-promo-text');
};

const roundAvailableCards = (team: IWidgetPlayer[], onRevealCard: (selnNumber: number) => void) =>
  team.map((player) => (
    <div key={player.selnNumber}>
      <PlayerCardWithFade
        selectionNumber={player.selnNumber.toString()}
        name={player.displayName}
        revealed={player.revealed}
        teamCode={player.teamCode}
        showCta={true}
        width={PlayerCardWithFadeWidths.fiftyEight}
        onRevealAnimationEndCallBack={() => onRevealCard(player.selnNumber)}
      />
      <WidgetTeamFooter revealed={player.revealed} teamCode={getOppositionTeam(player.teamCode, player.eventParticipants)} />
    </div>
  ));

const roundUnavailableCards = () =>
  [1, 2, 3, 4, 5].map((num) => {
    return (
      <div key={num} onClickCapture={(event) => event.stopPropagation()}>
        <PlayerCardWithFade width={PlayerCardWithFadeWidths.fiftyEight} />
        <WidgetTeamFooter revealed={false} teamCode="" />
      </div>
    );
  });

export default WidgetCardContainer;
